<script setup>
import { computed, ref, onUnmounted } from "vue";
import * as Yup from "yup";
import { Form, Field } from "vee-validate";
import { useTipStore } from "@/store/tip-store";
import { useRoute } from "vue-router";
import http from "axios";
import router from "@/router";

let apiURL = process.env.VUE_APP_API_URL;
const tipStore = useTipStore();
const route = useRoute();
const twitchName = ref();
const tipAmount = ref(5);
const message = ref("");
const alert = ref(false);
const alertError = ref(false);
const alertMessage = ref("");
const hideform = ref(false);
const customAmount = ref();
const tipRows = ref([]);
const checkout = ref();
const tipSet = computed(() => {
  return tipStore.state.tipSet;
});

onUnmounted(() => {
  if (tipSet.value) {
    destroyCheckout();
  }
});

const schema = computed(() => {
  var ccRules = {
    twitchName: Yup.string().required("Name is required").trim(),
  };

  var tipRule =
    tipAmount.value == 0
      ? {
          customAmount: Yup.number()
            .min(5, "Tip amount must greater than or equal to 5")
            .required("Tip amount is required "),
        }
      : [];

  var rules = {
    ...ccRules,
    ...tipRule,
  };

  return Yup.object().shape(rules);
});

if (route.params.sessionID) {
  savePayment();
}

const stripe = Stripe(process.env.VUE_APP_STRIPE_PK);

getTips();
if (tipSet.value) {
  setTip(tipStore.state.vars);
}

// Create a Checkout Session
async function setTip(tipVars) {
  let formData = new FormData();
  formData.append("tipAmount", tipVars.tipAmount);
  formData.append("twitchName", tipVars.twitchName);
  formData.append("message", tipVars.message);
  const fetchClientSecret = async () => {
    const response = await fetch(apiURL + "setSession", {
      method: "POST",
      body: formData,
    });
    const { clientSecret } = await response.json();
    return clientSecret;
  };

  checkout.value = await stripe.initEmbeddedCheckout({
    fetchClientSecret,
  });

  // Mount Checkout
  checkout.value.mount("#checkout");
}

function setTipAmount() {
  var amount = tipAmount.value > 0 ? tipAmount.value : customAmount.value;
  var tipVars = {
    tipAmount: amount,
    twitchName: twitchName.value,
    message: message.value,
  };
  tipStore.setTip(tipVars);
  setTip(tipVars);
}

async function savePayment() {
  let form = new FormData();
  form.append("sessionID", route.params.sessionID);
  hideform.value = true;
  try {
    const resp = await http.post(process.env.VUE_APP_API_URL + "saveTip", form);
    alert.value = true;
    alertMessage.value = "Thank you for the $" + resp.data.amount + " tip!";

    if (import.meta.env.MODE == "production") {
      await window._paq.push([
        "trackEvent",
        "Tip",
        "Tip submitted " + resp.data.amount,
      ]);
    }
    router.push({ path: "/tip", replace: true });
    setInterval(() => {
      alert.value = false;
      hideform.value = false;
    }, 9000);
  } catch (err) {
    alert.value = true;
    alertError.value = true;
    alertMessage.value = err.response.data.message;
  }
}

function resetForm() {
  tipStore.clearTip();
  destroyCheckout();
}

async function getTips() {
  try {
    const resp = await http.get(
      process.env.VUE_APP_API_URL + "get-latest-tips"
    );
    tipRows.value = resp.data.tipRows;
  } catch (err) {
    console.log(err);
  }
}

function formatNumber(Amount) {
  parseFloat(Amount);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Amount);
}

function destroyCheckout() {
  checkout.value.destroy();
}
</script>

<template>
  <div class="tip-page">
    <!-- BACKGROUND DECORATION -->

    <div class="tip-background-text" aria-hidden="true">SUPPORT</div>

    <div class="tip-glow" aria-hidden="true"></div>

    <div class="container position-relative">
      <!-- ==================================================
                 HEADER
            =================================================== -->

      <div class="tip-header text-center" data-aos="fade-up" v-if="!hideform">
        <div class="section-eyebrow justify-content-center">
          <span></span>
          Support DJ Saturn
          <span></span>
        </div>

        <h1 class="tip-title">
          <template v-if="!tipSet">
            Show Some
            <span>Love.</span>
          </template>

          <template v-else>
            Tip
            <span>DJ Saturn.</span>
          </template>
        </h1>

        <p v-if="!tipSet" class="tip-intro mx-auto">
          Enjoying the music? Leave a tip and show some love for DJ Saturn.
        </p>

        <button
          v-if="tipSet"
          type="button"
          class="btn tip-reset-btn"
          @click="resetForm"
        >
          <i class="bi bi-arrow-counterclockwise"></i>
          Start Over
        </button>
      </div>

      <!-- ==================================================
                 ALERT
            =================================================== -->

      <div
        v-if="alert"
        class="tip-alert"
        :class="{
          'tip-alert-error': alertError,
          'tip-alert-success': !alertError,
        }"
        role="alert"
        v-html="alertMessage"
      ></div>

      <div v-if="!hideform">
        <!-- ==================================================
                     TIP FORM
                =================================================== -->

        <div v-show="!tipSet" class="row g-4 g-xl-5 align-items-start">
          <div class="col-lg-8">
            <div class="tip-panel" data-aos="fade-up">
              <Form
                @submit="setTipAmount"
                :validation-schema="schema"
                v-slot="{ errors }"
              >
                <!-- AMOUNT -->

                <div class="tip-form-section">
                  <div class="tip-step">
                    <span class="tip-step-number"> 01 </span>

                    <div>
                      <h2>Choose your tip</h2>

                      <p>Select an amount or enter your own.</p>
                    </div>
                  </div>

                  <div class="tip-amount-grid">
                    <input
                      id="five"
                      v-model="tipAmount"
                      type="radio"
                      class="btn-check"
                      name="tipAmount"
                      value="5"
                      autocomplete="off"
                    />

                    <label class="tip-amount" for="five">
                      <span class="currency"> $ </span>

                      <strong>5</strong>

                      <small> Coffee </small>
                    </label>

                    <input
                      id="ten"
                      v-model="tipAmount"
                      type="radio"
                      class="btn-check"
                      name="tipAmount"
                      value="10"
                      autocomplete="off"
                    />

                    <label class="tip-amount" for="ten">
                      <span class="currency"> $ </span>

                      <strong>10</strong>

                      <small> Good Vibes </small>
                    </label>

                    <input
                      id="twenty"
                      v-model="tipAmount"
                      type="radio"
                      class="btn-check"
                      name="tipAmount"
                      value="20"
                      autocomplete="off"
                    />

                    <label class="tip-amount" for="twenty">
                      <span class="currency"> $ </span>

                      <strong>20</strong>

                      <small> Lunch </small>
                    </label>

                    <input
                      id="forty"
                      v-model="tipAmount"
                      type="radio"
                      class="btn-check"
                      name="tipAmount"
                      value="40"
                      autocomplete="off"
                    />

                    <label class="tip-amount" for="forty">
                      <span class="currency"> $ </span>

                      <strong>40</strong>

                      <small> Big Support </small>
                    </label>

                    <input
                      id="other"
                      v-model="tipAmount"
                      type="radio"
                      class="btn-check"
                      name="tipAmount"
                      value="0"
                      autocomplete="off"
                    />

                    <label class="tip-amount tip-amount-other" for="other">
                      <i class="bi bi-plus-lg"></i>

                      <strong> Other </strong>

                      <small> Your amount </small>
                    </label>
                  </div>

                  <!-- CUSTOM AMOUNT -->

                  <div v-if="tipAmount == 0" class="custom-amount mt-4">
                    <div class="input-group input-group-lg">
                      <span class="input-group-text"> $ </span>

                      <div class="form-floating">
                        <Field
                          id="custom-amount"
                          v-model="customAmount"
                          type="text"
                          class="form-control"
                          name="customAmount"
                          placeholder="Tip amount"
                          :class="{
                            'is-invalid': errors.customAmount,
                          }"
                        />

                        <label for="custom-amount"> Custom amount </label>
                      </div>
                    </div>

                    <div
                      v-if="errors.customAmount"
                      class="invalid-feedback d-block"
                    >
                      {{ errors.customAmount }}
                    </div>
                  </div>
                </div>

                <div class="tip-divider"></div>

                <!-- YOUR INFO -->

                <div class="tip-form-section">
                  <div class="tip-step">
                    <span class="tip-step-number"> 02 </span>

                    <div>
                      <h2>Add your name</h2>

                      <p>Let me know who's showing the love.</p>
                    </div>
                  </div>

                  <div
                    class="form-floating mb-3"
                    :class="{
                      'is-invalid': errors.twitchName,
                    }"
                  >
                    <Field
                      id="twitch-name"
                      v-model="twitchName"
                      type="text"
                      class="form-control"
                      name="twitchName"
                      placeholder="Your name"
                      :class="{
                        'is-invalid': errors.twitchName,
                      }"
                    />

                    <label for="twitch-name"> Your name </label>

                    <div class="invalid-feedback">
                      {{ errors.twitchName }}
                    </div>

                    <div v-if="!errors.twitchName" class="form-text">
                      Use your Twitch username if available.
                    </div>
                  </div>

                  <div class="form-floating">
                    <textarea
                      id="tipMessage"
                      v-model="message"
                      class="form-control"
                      placeholder="
                                                Leave a message
                                            "
                      maxlength="255"
                    ></textarea>

                    <label for="tipMessage">
                      Leave a message with your tip
                    </label>
                  </div>

                  <div class="tip-character-limit">
                    {{ message?.length || 0 }}
                    / 255
                  </div>
                </div>

                <!-- SUBMIT -->

                <button type="submit" class="btn btn-tip btn-lg w-100">
                  Continue to Tip

                  <i class="bi bi-arrow-right"></i>
                </button>
              </Form>
            </div>
          </div>

          <!-- ==================================================
                         LATEST TIPS
                    =================================================== -->

          <div class="col-lg-4">
            <aside class="latest-tips" data-aos="fade-up" data-aos-delay="100">
              <div class="latest-tips-header">
                <div>
                  <div class="latest-label">Much Appreciated</div>

                  <h2>Latest Tips</h2>
                </div>

                <i class="bi bi-heart-fill latest-heart"></i>
              </div>

              <div v-if="tipRows.length" class="latest-tip-list">
                <div
                  v-for="(tipRow, index) in tipRows"
                  :key="'tip-' + index"
                  class="latest-tip-row"
                >
                  <div class="tip-avatar">
                    {{ tipRow.t_name?.charAt(0).toUpperCase() }}
                  </div>

                  <div class="latest-tip-name">
                    {{ tipRow.t_name }}
                  </div>

                  <div class="latest-tip-amount">
                    ${{ formatNumber(tipRow.t_amt) }}
                  </div>
                </div>
              </div>

              <div v-else class="latest-tips-empty">
                <i class="bi bi-heart"></i>

                <p>Be the first to show some love.</p>
              </div>

              <div class="latest-tips-footer">
                <i class="bi bi-stars"></i>

                Thank you for supporting DJ Saturn.
              </div>
            </aside>
          </div>
        </div>

        <!-- ==================================================
                     CHECKOUT
                =================================================== -->

        <div id="checkout" v-show="tipSet" class="tip-checkout">
          <!-- Payment form inserted here -->
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
/* ==========================================================
   TIP PAGE
========================================================== */

.tip-page {
  --tip-accent: #0b71af;
  --tip-accent-hover: #1388cc;

  position: relative;

  min-height: 100vh;

  padding: 5rem 0 8rem;

  overflow: hidden;

  background: radial-gradient(
      circle at 75% 20%,
      rgba(11, 113, 175, 0.12),
      transparent 30%
    ),
    #090909;

  color: #fff;
}

/* ==========================================================
   BACKGROUND
========================================================== */

.tip-background-text {
  position: absolute;

  top: 7rem;
  left: 50%;

  transform: translateX(-50%);

  color: rgba(255, 255, 255, 0.018);

  font-size: clamp(8rem, 22vw, 22rem);

  font-weight: 900;
  line-height: 1;

  letter-spacing: -0.08em;
  text-transform: uppercase;
  white-space: nowrap;

  pointer-events: none;
  user-select: none;
}

.tip-glow {
  position: absolute;

  top: 15%;
  right: -250px;

  width: 600px;
  height: 600px;

  border-radius: 50%;

  background: rgba(11, 113, 175, 0.12);

  filter: blur(100px);

  pointer-events: none;
}

/* ==========================================================
   HEADER
========================================================== */

.tip-header {
  position: relative;
  z-index: 2;

  max-width: 800px;

  margin: 0 auto 4rem;
}

.tip-title {
  margin-bottom: 1.25rem;

  color: #fff;

  font-size: clamp(3.5rem, 8vw, 7rem);

  font-weight: 900;
  line-height: 0.88;

  letter-spacing: -0.06em;

  text-transform: uppercase;
}

.tip-title span {
  display: block;

  color: var(--tip-accent);
}

.tip-intro {
  max-width: 620px;

  color: rgba(255, 255, 255, 0.6);

  font-size: 1.05rem;
  line-height: 1.75;
}

/* ==========================================================
   RESET
========================================================== */

.tip-reset-btn {
  margin-top: 1rem;

  border: 1px solid rgba(255, 255, 255, 0.15);

  color: #fff;
}

.tip-reset-btn:hover {
  border-color: rgba(255, 255, 255, 0.3);

  background: rgba(255, 255, 255, 0.05);

  color: #fff;
}

/* ==========================================================
   PANEL
========================================================== */

.tip-panel,
.latest-tips,
.tip-checkout {
  border: 1px solid rgba(255, 255, 255, 0.09);

  background: rgba(255, 255, 255, 0.025);

  backdrop-filter: blur(10px);
}

.tip-panel {
  padding: 3rem;
}

/* ==========================================================
   STEPS
========================================================== */

.tip-step {
  display: flex;
  align-items: flex-start;

  gap: 1rem;

  margin-bottom: 2rem;
}

.tip-step-number {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 42px;
  height: 42px;

  flex: 0 0 42px;

  border: 1px solid rgba(11, 113, 175, 0.45);

  color: var(--tip-accent);

  font-size: 0.7rem;
  font-weight: 800;

  letter-spacing: 0.08rem;
}

.tip-step h2 {
  margin: 0 0 0.25rem;

  color: #fff;

  font-size: 1.3rem;
  font-weight: 800;
}

.tip-step p {
  margin: 0;

  color: rgba(255, 255, 255, 0.45);

  font-size: 0.85rem;
}

/* ==========================================================
   AMOUNTS
========================================================== */

.tip-amount-grid {
  display: grid;

  grid-template-columns: repeat(5, 1fr);

  gap: 0.75rem;
}

.tip-amount {
  position: relative;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  min-height: 120px;

  padding: 1rem 0.5rem;

  border: 1px solid rgba(255, 255, 255, 0.1);

  background: rgba(255, 255, 255, 0.02);

  color: #fff;

  cursor: pointer;

  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}

.tip-amount:hover {
  border-color: rgba(11, 113, 175, 0.55);

  background: rgba(11, 113, 175, 0.06);

  transform: translateY(-2px);
}

.tip-amount .currency {
  position: absolute;

  top: 0.9rem;
  left: 0.8rem;

  color: rgba(255, 255, 255, 0.4);

  font-size: 0.75rem;
}

.tip-amount strong {
  font-size: 1.75rem;
  font-weight: 900;
}

.tip-amount small {
  margin-top: 0.2rem;

  color: rgba(255, 255, 255, 0.4);

  font-size: 0.65rem;

  letter-spacing: 0.04rem;
  text-transform: uppercase;
}

.tip-amount-other i {
  margin-bottom: 0.4rem;

  color: var(--tip-accent);

  font-size: 1.2rem;
}

.tip-amount-other strong {
  font-size: 1rem;
}

/* SELECTED */

.btn-check:checked + .tip-amount {
  border-color: var(--tip-accent);

  background: rgba(11, 113, 175, 0.14);

  box-shadow: inset 0 0 0 1px rgba(11, 113, 175, 0.2);
}

.btn-check:checked + .tip-amount::after {
  content: "\F26A";

  position: absolute;

  top: 0.6rem;
  right: 0.65rem;

  color: var(--tip-accent);

  font-family: "bootstrap-icons";

  font-size: 0.85rem;
}

/* ==========================================================
   DIVIDER
========================================================== */

.tip-divider {
  height: 1px;

  margin: 3rem 0;

  background: rgba(255, 255, 255, 0.08);
}

/* ==========================================================
   FORM
========================================================== */

.tip-page .form-control,
.tip-page .input-group-text {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: #101010;
  color: #fff;
}

.tip-page .form-control {
  min-height: 58px;
}

.tip-page textarea.form-control {
  height: 130px;
  min-height: 130px;
}

.tip-page .form-control,
.tip-page .input-group-text {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: #101010;
  color: #fff;
}

.tip-page .form-control.is-invalid {
  border-color: var(--bs-form-invalid-border-color);
}

.tip-page .form-control.is-invalid:focus {
  border-color: var(--bs-form-invalid-border-color);
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-danger-rgb), 0.25);
}

.tip-page .form-control:focus {
  border-color: var(--tip-accent);

  background: #101010;

  color: #fff;

  box-shadow: 0 0 0 0.2rem rgba(11, 113, 175, 0.12);
}

.tip-page .form-floating > label {
  color: rgba(255, 255, 255, 0.5);
}

.tip-page .form-text {
  color: rgba(255, 255, 255, 0.4);
}

.tip-page .input-group-text {
  color: var(--tip-accent);

  font-weight: 800;
}

.tip-character-limit {
  margin-top: 0.4rem;

  color: rgba(255, 255, 255, 0.3);

  font-size: 0.7rem;

  text-align: right;
}

/* ==========================================================
   SUBMIT
========================================================== */

.btn-tip {
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 0.75rem;

  margin-top: 2.5rem;

  padding: 1rem 1.5rem;

  border-color: var(--tip-accent);

  background: var(--tip-accent);

  color: #fff;

  font-size: 0.85rem;
  font-weight: 800;

  letter-spacing: 0.06rem;
  text-transform: uppercase;

  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-tip:hover {
  border-color: var(--tip-accent-hover);

  background: var(--tip-accent-hover);

  color: #fff;

  transform: translateY(-2px);

  box-shadow: 0 15px 40px rgba(11, 113, 175, 0.25);
}

/* ==========================================================
   LATEST TIPS
========================================================== */

.latest-tips {
  position: sticky;

  top: 2rem;

  overflow: hidden;
}

.latest-tips-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 1.75rem 1.5rem;

  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.latest-label {
  margin-bottom: 0.2rem;

  color: var(--tip-accent);

  font-size: 0.6rem;
  font-weight: 800;

  letter-spacing: 0.12rem;
  text-transform: uppercase;
}

.latest-tips-header h2 {
  margin: 0;

  color: #fff;

  font-size: 1.25rem;
  font-weight: 800;
}

.latest-heart {
  color: var(--tip-accent);

  font-size: 1.2rem;
}

/* ==========================================================
   TIP ROW
========================================================== */

.latest-tip-row {
  display: grid;

  grid-template-columns: 38px 1fr auto;

  align-items: center;

  gap: 0.75rem;

  padding: 1rem 1.5rem;

  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.tip-avatar {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 38px;
  height: 38px;

  border: 1px solid rgba(11, 113, 175, 0.35);

  border-radius: 50%;

  background: rgba(11, 113, 175, 0.1);

  color: var(--tip-accent);

  font-size: 0.75rem;
  font-weight: 800;
}

.latest-tip-name {
  overflow: hidden;

  color: rgba(255, 255, 255, 0.75);

  font-size: 0.85rem;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.latest-tip-amount {
  color: #fff;

  font-size: 0.85rem;
  font-weight: 800;
}

/* ==========================================================
   LATEST TIPS FOOTER
========================================================== */

.latest-tips-footer {
  padding: 1rem 1.5rem;

  background: rgba(11, 113, 175, 0.05);

  color: rgba(255, 255, 255, 0.4);

  font-size: 0.7rem;
}

.latest-tips-footer i {
  margin-right: 0.4rem;

  color: var(--tip-accent);
}

.latest-tips-empty {
  padding: 3rem 1.5rem;

  color: rgba(255, 255, 255, 0.4);

  text-align: center;
}

.latest-tips-empty i {
  display: block;

  margin-bottom: 0.75rem;

  color: var(--tip-accent);

  font-size: 1.5rem;
}

/* ==========================================================
   ALERT
========================================================== */

.tip-alert {
  max-width: 850px;

  margin: 0 auto 2rem;

  padding: 1rem 1.25rem;

  border: 1px solid;
}

.tip-alert-success {
  border-color: rgba(25, 135, 84, 0.35);

  background: rgba(25, 135, 84, 0.08);

  color: #75d5a5;
}

.tip-alert-error {
  border-color: rgba(220, 53, 69, 0.35);

  background: rgba(220, 53, 69, 0.08);

  color: #ff8c98;
}

/* ==========================================================
   CHECKOUT
========================================================== */

.tip-checkout {
  max-width: 850px;

  margin: 0 auto;

  padding: 2rem;
}

/* ==========================================================
   TABLET
========================================================== */

@media (max-width: 991.98px) {
  .tip-page {
    padding: 5rem 0 6rem;
  }

  .tip-amount-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .latest-tips {
    position: static;
  }
}

/* ==========================================================
   MOBILE
========================================================== */

@media (max-width: 575.98px) {
  .tip-page {
    padding: 3.5rem 0 4rem;
  }

  .tip-header {
    margin-bottom: 2.5rem;
  }

  .tip-title {
    font-size: clamp(3rem, 15vw, 4.5rem);
  }

  .tip-intro {
    font-size: 0.95rem;
  }

  .tip-panel {
    padding: 2rem 1.25rem;
  }

  .tip-amount-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .tip-amount {
    min-height: 105px;
  }

  .tip-amount-other {
    grid-column: 1 / -1;
  }

  .tip-divider {
    margin: 2.25rem 0;
  }

  .tip-step-number {
    width: 38px;
    height: 38px;

    flex-basis: 38px;
  }

  .tip-step h2 {
    font-size: 1.1rem;
  }
}

/* ==========================================================
   REDUCED MOTION
========================================================== */

@media (prefers-reduced-motion: reduce) {
  .tip-amount,
  .btn-tip {
    transition: none;
  }

  .tip-amount:hover,
  .btn-tip:hover {
    transform: none;
  }
}
</style>
